import React, { useCallback } from "react";

import { useDispatch } from "react-redux";

import CheckboxLabel from "@busbud/design-system-components/dist/CheckboxLabel";

import { getSuggestionLocationIcon } from "@app/components/search-form-hydrated/autocomplete-input-hydrated/autocomplete-option/autocomplete-icons";
import { useAppContext } from "@app/helpers/hooks";
import { SuggestionIcon } from "@app/modules/search/suggestion/suggestion-icon";

import { FilterCollapse } from "./filter-collapse";
import { useFacetedSearch } from "./hooks/useFacetedSearch";
import { useResultsSelector } from "../../hooks/use-results-selector";
import { getPointsOfInterest } from "../../selectors/get-points-of-interest";
import { getSelectedDestinationPointsOfInterests } from "../../selectors/get-selected-destination-points-of-interests";
import { getSelectedOriginPointsOfInterests } from "../../selectors/get-selected-origin-points-of-interests";
import { setDestinationPoi, setOriginPoi } from "../../state/filters";

interface PointOfInterestFilterProps {
  point_of_interest_type: "origin" | "destination";
}

const ORIGIN_POI_TYPE = "origin";

export const PointOfInterestFilter: React.FC<PointOfInterestFilterProps> = ({
  point_of_interest_type
}) => {
  const { translator } = useAppContext();
  const dispatch = useDispatch();
  const faceted_search = useFacetedSearch();
  const aggregation = faceted_search.getAggregation(
    `${point_of_interest_type}_poi`
  );
  const points_of_interest = useResultsSelector(getPointsOfInterest);
  const state_filter_poi = useResultsSelector(state => {
    return point_of_interest_type === ORIGIN_POI_TYPE
      ? getSelectedOriginPointsOfInterests(state)
      : getSelectedDestinationPointsOfInterests(state);
  });

  const getCheckboxValue = useCallback(
    (key: string | number) => state_filter_poi.includes(String(key)),
    [state_filter_poi]
  );

  function handleCheckboxChange(key: string) {
    const setPoiFilters =
      point_of_interest_type === ORIGIN_POI_TYPE
        ? setOriginPoi
        : setDestinationPoi;

    const state_filtered_poi = state_filter_poi.includes(key)
      ? state_filter_poi.filter(id => id !== key)
      : [...state_filter_poi, key];

    const poi_payload = state_filtered_poi.reduce((acc, poi) => {
      return {
        ...acc,
        // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        [poi]: points_of_interest[poi].name
      };
    }, {});

    return dispatch(setPoiFilters(poi_payload));
  }

  const buckets_data = React.useMemo(() => {
    return aggregation?.buckets.map(({ key, doc_count }) => {
      // @ts-expect-error TS2339 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const { name, type } = points_of_interest[key];
      const icon = (
        <SuggestionIcon
          size="sm"
          type={getSuggestionLocationIcon(type)}
          hidden={false}
        />
      );
      return {
        key,
        disabled: doc_count <= 0,
        value: getCheckboxValue(key),
        label: `${name} (${doc_count})`,
        icon
      };
    });
  }, [aggregation?.buckets, points_of_interest, getCheckboxValue]);

  // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  if (buckets_data.length <= 1) {
    return null;
  }

  const filter_title =
    point_of_interest_type === ORIGIN_POI_TYPE
      ? translator.t("!results.filters.departures.title")
      : translator.t("!results.filters.arrivals.title");

  return (
    <FilterCollapse title={filter_title}>
      {buckets_data?.map(({ key, disabled, value, label, icon }) => (
        <div key={key} className="flex items-center justify-between">
          <CheckboxLabel
            outlined
            shape="square"
            disabled={disabled}
            startIcon={icon}
            label={label}
            value={value}
            checked={value}
            onChange={() => handleCheckboxChange(String(key))}
          />
        </div>
      ))}
    </FilterCollapse>
  );
};
